import { useState, useEffect, useRef, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useUserRole } from '@/hooks/useUserRole'
import apiClient from '@/lib/api/apiClient'
import { parseCompanyOwnersApiResponse } from '../utils/companyOwnersApi'
import { getStates, type State } from '@/lib/api/statesService'
import { geocodeAddressRaw, matchZipAndStateAngularStyle } from '@/lib/utils/geocoding'
import { mapGooglePlaceToPhysicalAddressFields } from '@/lib/utils/googlePlaceAddress'
import { shouldShowMapOnEditLoad } from '@/lib/utils/showMapOnEdit'
import { loadGoogleMapsScript } from '@/lib/utils/googleMaps'
import { showSuccess, showError, formatApiError, showValidationMessage } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { MAX_PAYMENT_FILE_SIZE_MB } from '../constants'
import { isCompanyDuplicateAddressCancelledError } from '@/features/company/utils/companyDuplicateAddress'
import { getFilterValueAngularStyle } from '../utils/companyValidators'
import type { CompanyAddFormValues, CompanyInfoTabProps, CompanyOwnerRow } from '../types'
import {
    ADDRESS_VERIFY_HTML,
    ERROR_ADDRESS_VERIFY,
    PHONE_FORMAT_ERROR,
    ZIPCODE_FORMAT_ERROR,
    buildCompanyInfoFormValues,
    buildCompanyInfoSavePayload,
    buildFullPhysicalAddress,
    clearMailingAddressFormFields,
    isPhysicalAddressField,
    isValidPhoneForSave,
    isValidPhysicalZipcode,
    resolveCompanyInfoId,
} from '../utils/companyInfoTabLogic'

export function useCompanyInfoTab({ company, companyId: companyIdProp, onUpdate, onRefresh }: CompanyInfoTabProps) {
    const { hasPolicy, isLibertyRole, isPropertyRole } = useUserRole()
    const [states, setStates] = useState<State[]>([])
    const [, setIsSubmitting] = useState(false)
    const [showValidationModal, setShowValidationModal] = useState(false)
    const [validationModalMessage, setValidationModalMessage] = useState('')
    const [validationModalTitle, setValidationModalTitle] = useState('Validation Message')
    /** Persists after the validation modal closes (Angular `mapMessage`). */
    const [addressValidationMessage, setAddressValidationMessage] = useState('')
    const [showDisableConfirm, setShowDisableConfirm] = useState(false)
    const [showMap, setShowMap] = useState(false)
    const [w9File, setW9File] = useState<File | null>(null)
    const [w9Uploading, setW9Uploading] = useState(false)
    const [deleteW9Confirm, setDeleteW9Confirm] = useState<{ isOpen: boolean; fileId: string }>({
        isOpen: false,
        fileId: '',
    })
    const w9InputRef = useRef<HTMLInputElement>(null)
    const streetAddressRef = useRef<HTMLInputElement>(null)
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
    const [isAddressManuallyChanged, setIsAddressManuallyChanged] = useState(false)
    const [isValidatingAddress, setIsValidatingAddress] = useState(false)
    const [showGoogleAddress, setShowGoogleAddress] = useState(false)
    const [streetAddress, setStreetAddress] = useState('')
    const [enableSetAddress, setEnableSetAddress] = useState(false)
    const [showLatLongLookup, setShowLatLongLookup] = useState(false)
    const [isMapMessage, setIsMapMessage] = useState(false)
    const [placeDetails, setPlaceDetails] = useState<google.maps.places.PlaceResult | null>(null)
    const mapRef = useRef<HTMLDivElement>(null)
    const mapInstance = useRef<google.maps.Map | null>(null)
    const markerInstance = useRef<google.maps.Marker | null>(null)

    const form = useForm<CompanyAddFormValues>({
        defaultValues: buildCompanyInfoFormValues(company, []),
        mode: 'onBlur',
        reValidateMode: 'onChange',
    })

    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        getValues,
        reset,
        trigger,
        setError,
        clearErrors,
        formState: { errors },
    } = form

    const companyId = resolveCompanyInfoId(companyIdProp, company)

    const prevCompanyRef = useRef(company)
    const prevStatesLenRef = useRef(states.length)

    useEffect(() => {
        if (!company) return
        const prevId = (prevCompanyRef.current as Record<string, unknown> | null | undefined)?.company_id
        const currId = (company as Record<string, unknown> | null | undefined)?.company_id
        const prevUpdated = (prevCompanyRef.current as Record<string, unknown> | null | undefined)?.updated_at
        const currUpdated = (company as Record<string, unknown> | null | undefined)?.updated_at

        const isNewCompany = !prevCompanyRef.current || prevId !== currId || prevUpdated !== currUpdated
        const statesLoaded = prevStatesLenRef.current === 0 && states.length > 0

        if (isNewCompany || statesLoaded) {
            reset(buildCompanyInfoFormValues(company, states))
            setShowMap(shouldShowMapOnEditLoad(company.latitude, company.longitude))
        }
        prevCompanyRef.current = company
        prevStatesLenRef.current = states.length
    }, [company, states, reset])

    const [ownersList, setOwnersList] = useState<CompanyOwnerRow[]>([])
    const [newOwnerName, setNewOwnerName] = useState('')
    const [ownersLoading, setOwnersLoading] = useState(false)

    const loadOwners = useCallback(async () => {
        if (!companyId) return
        setOwnersLoading(true)
        try {
            const res = await apiClient.get(`/api/company_owner/${companyId}`)
            const raw = res?.data ?? res
            setOwnersList(parseCompanyOwnersApiResponse(raw))
        } catch {
            setOwnersList([])
        } finally {
            setOwnersLoading(false)
        }
    }, [companyId])

    useEffect(() => {
        loadOwners()
    }, [loadOwners])

    const addOwner = async (e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        const name = newOwnerName.trim()
        if (!name) {
            showError('Please enter an owner name.')
            return
        }
        if (!companyId) {
            showError('Company ID is missing.')
            return
        }
        setOwnersLoading(true)
        try {
            await apiClient.post('/api/company_owner', {
                company_id: Number(companyId),
                company_owner: name,
            })
            showSuccess('Owner Added Successfully!')
            setNewOwnerName('')
            await loadOwners()
        } catch (err: unknown) {
            const e = err as {
                response?: { data?: { message?: unknown; error?: unknown } }
                message?: unknown
            }
            const msg =
                e?.response?.data?.message ??
                e?.response?.data?.error ??
                e?.message ??
                'Failed to add owner.'
            showError(typeof msg === 'string' ? msg : 'Failed to add owner.')
        } finally {
            setOwnersLoading(false)
        }
    }

    const deleteOwner = async (ownerId: number) => {
        if (!ownerId || !companyId) return
        setOwnersLoading(true)
        try {
            await apiClient.delete(`/api/company_owner/${ownerId}`)
            showSuccess('Owner Deleted Successfully!')
            await loadOwners()
        } catch (err: unknown) {
            const data = (err as { response?: { data?: unknown } })?.response?.data
            const msg =
                (data && typeof data === 'object' ? (data as { message?: unknown }).message : data) ??
                'Failed to delete owner.'
            showError(typeof msg === 'string' ? msg : 'Failed to delete owner.')
        } finally {
            setOwnersLoading(false)
        }
    }

    const watchLatitude = watch('latitude')
    const watchLongitude = watch('longitude')
    const watchIsMailingAddress = watch('is_mailing_address')
    const prevIsMailingAddressRef = useRef<boolean | undefined>(undefined)

    useEffect(() => {
        if (prevIsMailingAddressRef.current === true && watchIsMailingAddress === false) {
            clearMailingAddressFormFields(setValue, clearErrors)
        }
        prevIsMailingAddressRef.current = watchIsMailingAddress
    }, [watchIsMailingAddress, setValue, clearErrors])

    useEffect(() => {
        getStates().then(setStates).catch(console.error)
    }, [])

    useEffect(() => {
        const subscription = watch((_, { name }) => {
            if (isPhysicalAddressField(name)) {
                setIsAddressManuallyChanged(true)
            }
        })
        return () => subscription.unsubscribe()
    }, [watch])

    useEffect(() => {
        const initMap = async () => {
            if (!mapRef.current) return

            const lat = Number(watchLatitude) || 30.5557898
            const lng = Number(watchLongitude) || -87.8939053

            try {
                await loadGoogleMapsScript()
                if (window.google?.maps) {
                    if (!mapInstance.current) {
                        mapInstance.current = new window.google.maps.Map(mapRef.current, {
                            center: { lat, lng },
                            zoom: 14,
                        })
                        markerInstance.current = new window.google.maps.Marker({
                            position: { lat, lng },
                            map: mapInstance.current,
                        })
                    } else if (mapInstance.current && markerInstance.current) {
                        mapInstance.current.setCenter({ lat, lng })
                        markerInstance.current.setPosition({ lat, lng })
                    }
                }
            } catch (err) {
                console.error('Failed to load Google Maps API', err)
            }
        }

        initMap()
    }, [watchLatitude, watchLongitude])

    const onSubmit = async (data: CompanyAddFormValues) => {
        const officePhoneValid = isValidPhoneForSave(data.office_phone, { required: false })
        const primaryPhoneValid = isValidPhoneForSave(data.contact_mobile, { required: false })
        if (!officePhoneValid || !primaryPhoneValid) {
            if (!officePhoneValid) {
                setError('office_phone', { type: 'manual', message: PHONE_FORMAT_ERROR })
            } else {
                clearErrors('office_phone')
            }
            if (!primaryPhoneValid) {
                setError('contact_mobile', { type: 'manual', message: PHONE_FORMAT_ERROR })
            } else {
                clearErrors('contact_mobile')
            }
            return
        }
        clearErrors(['office_phone', 'contact_mobile'])

        if (!isValidPhysicalZipcode(data.zipcode)) {
            setError('zipcode', {
                type: 'manual',
                message: data.zipcode?.trim() ? ZIPCODE_FORMAT_ERROR : 'Zipcode is required',
            })
            return
        }
        clearErrors('zipcode')

        if (isAddressManuallyChanged) {
            showError('Please Validate Address before Saving')
            return
        }

        setIsSubmitting(true)
        try {
            const payload = getFilterValueAngularStyle(
                buildCompanyInfoSavePayload(data, company),
            ) as Record<string, unknown>
            // Angular `editCompany()` sets this after `getFilterValue` so `'0'` is never dropped.
            payload.is_mailing_address = data.is_mailing_address ? '1' : '0'
            await onUpdate(payload)
            showSuccess('Company Updated  Successfully!')
            setIsAddressManuallyChanged(false)
        } catch (error: unknown) {
            if (isCompanyDuplicateAddressCancelledError(error)) return
            showError(formatApiError(error))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleValidateAddress = async () => {
        const isValid = await trigger(['address1', 'city', 'state', 'zipcode'])
        if (!isValid) return
        const fullAddress = buildFullPhysicalAddress(watch())
        setStreetAddress(fullAddress)
        setShowGoogleAddress(true)
        setEnableSetAddress(true)
        setPlaceDetails(null)
        // Re-open Google lookup: unlock fields so incomplete results can be corrected.
        setShowLatLongLookup(false)
        if (streetAddressRef.current) streetAddressRef.current.value = fullAddress
        trackApplicationEvent('map_refreshed', 'company')
    }

    const applyGooglePlaceToForm = useCallback(
        (place: google.maps.places.PlaceResult) => {
            const mapped = mapGooglePlaceToPhysicalAddressFields(place)
            if (!mapped) return
            setValue('address1', mapped.address1, { shouldDirty: true, shouldValidate: true })
            setValue('address2', mapped.address2, { shouldDirty: true, shouldValidate: true })
            setValue('city', mapped.city, { shouldDirty: true, shouldValidate: true })
            setValue('state', mapped.state, { shouldDirty: true, shouldValidate: true })
            setValue('zipcode', mapped.zipcode, { shouldDirty: true, shouldValidate: true })
            if (place.formatted_address) setStreetAddress(place.formatted_address)
            setIsAddressManuallyChanged(true)
        },
        [setValue],
    )

    const handleSetGoogleAddress = async () => {
        if (placeDetails) applyGooglePlaceToForm(placeDetails)
        setEnableSetAddress(false)
        setShowLatLongLookup(true)
        // Surface missing required fields when Google returned an incomplete place.
        await trigger(['address1', 'city', 'state', 'zipcode'])
    }

    const handleAddressNotFound = async () => {
        setShowLatLongLookup(true)
        await trigger(['address1', 'city', 'state', 'zipcode'])
    }

    const failGeocodeLookup = (opts: { notify: boolean }) => {
        setShowMap(true)
        setIsMapMessage(true)
        setAddressValidationMessage(ERROR_ADDRESS_VERIFY)
        setIsAddressManuallyChanged(false)
        if (opts.notify) {
            // Angular `notify(addressVerify, 0)` → Validation Message popup
            setValidationModalTitle('Validation Message')
            setValidationModalMessage(ADDRESS_VERIFY_HTML)
            setShowValidationModal(true)
        }
    }

    const applyGeocodeSuccess = (lat: number, lng: number) => {
        setValue('latitude', lat, { shouldValidate: true })
        setValue('longitude', lng, { shouldValidate: true })
        setIsAddressManuallyChanged(false)
        setAddressValidationMessage('')
        setIsMapMessage(false)
        setShowMap(true)
    }

    const runGeocodeLookup = async (mode: 'LatLong' | 'refresh') => {
        const data = watch()
        const fullAddress = buildFullPhysicalAddress(data)
        setShowMap(true)
        setIsValidatingAddress(true)
        const notify = mode === 'LatLong'

        try {
            const { result, status } = await geocodeAddressRaw(fullAddress)

            if (
                status !== google.maps.GeocoderStatus.OK ||
                !result ||
                !result.geometry ||
                result.partial_match
            ) {
                failGeocodeLookup({ notify })
                return
            }

            const { zipValid, stateValid } = matchZipAndStateAngularStyle(
                result,
                data.zipcode,
                data.state,
            )

            if (!zipValid || !stateValid) {
                failGeocodeLookup({ notify })
                return
            }

            const lat = result.geometry.location.lat()
            const lng = result.geometry.location.lng()
            applyGeocodeSuccess(lat, lng)

            if (mode === 'LatLong') {
                // Angular `notifyError(..., 'Verification Successful!')` → popup, then save
                setValidationModalTitle('Verification Successful!')
                setValidationModalMessage(
                    `Address verified with Latitude: ${lat} Longitude: ${lng}`,
                )
                setShowValidationModal(true)
                await onSubmit({ ...getValues(), latitude: lat, longitude: lng })
            }
        } catch {
            failGeocodeLookup({ notify })
        } finally {
            setIsValidatingAddress(false)
        }
    }

    const handleLatLongLookup = async () => {
        const isValid = await trigger(['address1', 'city', 'state', 'zipcode'])
        if (!isValid) return
        await runGeocodeLookup('LatLong')
    }

    useEffect(() => {
        if (!showGoogleAddress || !streetAddressRef.current) return
        let cancelled = false
        const init = async () => {
            try {
                await loadGoogleMapsScript()
                if (cancelled || !window.google?.maps?.places || !streetAddressRef.current) return
                if (autocompleteRef.current) {
                    window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
                    autocompleteRef.current = null
                }
                autocompleteRef.current = new window.google.maps.places.Autocomplete(
                    streetAddressRef.current,
                    { componentRestrictions: { country: 'US' }, types: ['geocode'] },
                )
                autocompleteRef.current.setFields(['address_components', 'formatted_address'])
                window.google.maps.event.addListener(autocompleteRef.current, 'place_changed', () => {
                    const place = autocompleteRef.current?.getPlace()
                    if (!place) return
                    setPlaceDetails(place)
                    setEnableSetAddress(true)
                    if (place.formatted_address && streetAddressRef.current) {
                        streetAddressRef.current.value = place.formatted_address
                        setStreetAddress(place.formatted_address)
                    }
                })
            } catch {
                /* autocomplete optional */
            }
        }
        void init()
        return () => {
            cancelled = true
            if (autocompleteRef.current && window.google?.maps?.event) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
            }
            autocompleteRef.current = null
        }
    }, [showGoogleAddress])

    const uploadW9File = async (file: File) => {
        if (!companyId) {
            showError('Company ID is missing.')
            return
        }
        if (file.size / 1024 / 1024 > MAX_PAYMENT_FILE_SIZE_MB) {
            showValidationMessage('File Size Exceeds Allowable Limit of 2 MB.')
            if (w9InputRef.current) w9InputRef.current.value = ''
            setW9File(null)
            return
        }
        setW9Uploading(true)
        try {
            const formData = new FormData()
            formData.append('w9_file[0]', file)
            formData.append('type', 'COMPANY')
            const federalId = watch('federal_id')
            if (federalId) formData.append('federal_id', federalId)
            await apiClient.post(`/api/upload_ach_form/${companyId}`, formData)
            showSuccess('File Uploaded Successfully!')
            setW9File(null)
            if (w9InputRef.current) w9InputRef.current.value = ''
            onRefresh?.({ skipLoading: true })
        } catch (err: unknown) {
            const data = (err as { response?: { data?: unknown } })?.response?.data
            const msg =
                (data && typeof data === 'object' ? (data as { message?: unknown }).message : data) ??
                'Failed to upload W-9 file.'
            showError(typeof msg === 'string' ? msg : 'Failed to upload W-9 file.')
        } finally {
            setW9Uploading(false)
        }
    }

    const previewW9File = async (fileId: string | number, fileName?: string) => {
        if (!companyId) return
        const name = (fileName || 'W-9').trim() || 'W-9'
        try {
            // Angular company downloadFile: GET /serve-file/COMPANY/:id/W-9/:fileId with auth token.
            // Open via /file-handlers with a blob URL so preview + toolbar download both work.
            const { data } = await apiClient.get<Blob>(
                `/serve-file/COMPANY/${companyId}/W-9/${fileId}`,
                { responseType: 'blob' },
            )
            const blobUrl = window.URL.createObjectURL(data)
            const ext = name.split('.').pop()?.toLowerCase() || ''
            const isDoc = ['pdf', 'xlsx', 'xls', 'csv', 'doc', 'docx', 'txt'].includes(ext)
            if (isDoc) {
                window.open(blobUrl, '_blank', 'noopener,noreferrer')
            } else {
                const fileHandlersUrl =
                    `/file-handlers?path=${encodeURIComponent(blobUrl)}` +
                    `&name=${encodeURIComponent(name)}`
                window.open(fileHandlersUrl, '_blank', 'noopener,noreferrer')
            }
        } catch (err: unknown) {
            showError(formatApiError(err))
        }
    }

    const requestDeleteW9File = (fileId: string | number) => {
        setDeleteW9Confirm({ isOpen: true, fileId: String(fileId) })
    }

    const executeDeleteW9File = async () => {
        const fileId = deleteW9Confirm.fileId
        setDeleteW9Confirm({ isOpen: false, fileId: '' })
        if (!fileId) return
        try {
            // Angular deleteFile for w9_file → DELETE /api/delete_ach_form/:fileId
            await apiClient.delete(`/api/delete_ach_form/${fileId}`)
            showSuccess('Document file Removed')
            onRefresh?.({ skipLoading: true })
        } catch (err: unknown) {
            showError(formatApiError(err))
        }
    }

    const cancelDeleteW9File = () => {
        setDeleteW9Confirm({ isOpen: false, fileId: '' })
    }

    const refreshMap = () => {
        void runGeocodeLookup('refresh')
    }

    const cancelDisableConfirm = () => {
        setValue('listed', '1')
        setShowDisableConfirm(false)
    }

    const canEditInfo = hasPolicy(['company_info_edit'])
    const canEditListed = hasPolicy(['company_listed_edit'])

    return {
        company,
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        errors,
        clearErrors,
        setError,
        states,
        canEditInfo,
        canEditListed,
        isLibertyRole,
        isPropertyRole,
        showMap,
        isMapMessage,
        showValidationModal,
        setShowValidationModal,
        validationModalMessage,
        validationModalTitle,
        addressValidationMessage,
        showDisableConfirm,
        setShowDisableConfirm,
        w9File,
        setW9File,
        w9Uploading,
        w9InputRef,
        deleteW9Confirm,
        requestDeleteW9File,
        executeDeleteW9File,
        cancelDeleteW9File,
        isValidatingAddress,
        mapRef,
        watchIsMailingAddress,
        ownersList,
        newOwnerName,
        setNewOwnerName,
        ownersLoading,
        addOwner,
        deleteOwner,
        handleValidateAddress,
        handleLatLongLookup,
        handleSetGoogleAddress,
        handleAddressNotFound,
        showGoogleAddress,
        streetAddress,
        setStreetAddress,
        streetAddressRef,
        enableSetAddress,
        showLatLongLookup,
        uploadW9File,
        previewW9File,
        onSubmit,
        refreshMap,
        cancelDisableConfirm,
    }
}
